import { useCallback, useRef } from 'react';

/**
 * useAudioCache — persists recorded audio blobs in IndexedDB so they survive
 * app backgrounding, screen lock, and interrupted uploads.
 *
 * Usage:
 *   const { saveToCache, loadFromCache, clearCache, hasPending } = useAudioCache();
 *
 *   // After recording stops:
 *   await saveToCache(audioBlob, { familyCircleId, title });
 *
 *   // In handleUpload — pull from cache if blob is null (app was killed):
 *   const cached = await loadFromCache();
 *   if (cached) { upload(cached.blob, cached.meta); }
 *
 *   // After successful upload:
 *   await clearCache();
 */

const DB_NAME = 'echomemoir-audio-cache';
const DB_VERSION = 1;
const STORE_NAME = 'pending-uploads';
const CACHE_KEY = 'current';

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const useAudioCache = () => {
  const dbRef = useRef(null);

  const getDB = useCallback(async () => {
    if (!dbRef.current) {
      dbRef.current = await openDB();
    }
    return dbRef.current;
  }, []);

  /**
   * Save an audio blob + metadata to IndexedDB.
   * Call this immediately when recording stops, BEFORE upload starts.
   */
  const saveToCache = useCallback(async (blob, meta = {}) => {
    try {
      const db = await getDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      const record = {
        blob,
        meta: {
          ...meta,
          cachedAt: Date.now(),
          mimeType: blob.type || 'audio/webm',
          size: blob.size,
        },
      };

      store.put(record, CACHE_KEY);

      return new Promise((resolve, reject) => {
        tx.oncomplete = () => {
          console.log(`💾 Audio cached: ${(blob.size / 1024 / 1024).toFixed(1)}MB`);
          resolve(true);
        };
        tx.onerror = () => {
          console.error('Cache save failed:', tx.error);
          reject(tx.error);
        };
      });
    } catch (err) {
      console.error('Failed to cache audio:', err);
      // Don't throw — caching failure shouldn't block the upload attempt
      return false;
    }
  }, [getDB]);

  /**
   * Load the cached audio blob + metadata.
   * Returns { blob, meta } or null if nothing cached.
   */
  const loadFromCache = useCallback(async () => {
    try {
      const db = await getDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(CACHE_KEY);

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const result = request.result;
          if (result && result.blob) {
            console.log(`📂 Loaded cached audio: ${(result.blob.size / 1024 / 1024).toFixed(1)}MB, cached ${Math.round((Date.now() - result.meta.cachedAt) / 1000)}s ago`);
            resolve(result);
          } else {
            resolve(null);
          }
        };
        request.onerror = () => {
          console.error('Cache load failed:', request.error);
          resolve(null);
        };
      });
    } catch (err) {
      console.error('Failed to load cached audio:', err);
      return null;
    }
  }, [getDB]);

  /**
   * Clear the cache after a successful upload.
   */
  const clearCache = useCallback(async () => {
    try {
      const db = await getDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.delete(CACHE_KEY);

      return new Promise((resolve) => {
        tx.oncomplete = () => {
          console.log('🗑️ Audio cache cleared');
          resolve(true);
        };
        tx.onerror = () => {
          console.error('Cache clear failed:', tx.error);
          resolve(false);
        };
      });
    } catch (err) {
      console.error('Failed to clear cache:', err);
      return false;
    }
  }, [getDB]);

  /**
   * Check if there's a pending upload in the cache.
   * Returns meta object or null.
   */
  const hasPending = useCallback(async () => {
    try {
      const cached = await loadFromCache();
      if (cached && cached.blob && cached.blob.size > 0) {
        return cached.meta;
      }
      return null;
    } catch {
      return null;
    }
  }, [loadFromCache]);

  return {
    saveToCache,
    loadFromCache,
    clearCache,
    hasPending,
  };
};
